/**
 * SSOT Override Enforcement
 * 
 * Single enforcement point for narrative consistency.
 * Applied ONCE before any report rendering (UI + PDF).
 * 
 * DO NOT modify calculations — this is a response layer override only.
 */

// ═══════════════════════════════════════════════════════════════════════════
// GLOBAL CONFIDENCE MODE — gates ALL outputs based on detection strength
// ═══════════════════════════════════════════════════════════════════════════
// Modes:
//   'signal'   = 0–39 score: neutral deviation only, NO numbers, NO €
//   'evidence' = 40–69 score: soft numbers (~), ranges, "indicative"
//   'decision' = 70–100 score: exact numbers, direct wording, full € impact
//
// Rules:
//   - Snapshot Mode (single sample) ALWAYS forces 'signal' mode
//   - "Missing fish" wording ONLY allowed in 'decision' mode
//   - Financial impact ONLY shown in 'decision' mode (hidden in signal, indicative in evidence)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Resolve confidence mode from detection strength score
 * @param {number} detectionStrengthScore - Score 0-100
 * @param {boolean} isSnapshotMode - Single sample analysis flag
 * @returns {'signal' | 'evidence' | 'decision'}
 */
const resolveConfidenceMode = (detectionStrengthScore, isSnapshotMode) => {
  // Snapshot mode ALWAYS forces signal mode
  if (isSnapshotMode) return 'signal';
  
  const score = detectionStrengthScore ?? 0;
  if (score >= 70) return 'decision';
  if (score >= 40) return 'evidence';
  return 'signal';
};

/**
 * Format number based on confidence mode
 * @param {number} value - The raw number
 * @param {string} mode - 'signal' | 'evidence' | 'decision'
 * @param {string} type - 'count' | 'percent' | 'currency'
 * @returns {string | null} - Formatted string or null if hidden
 */
const formatByMode = (value, mode, type = 'count') => {
  if (value == null || value === 0) return null;
  
  if (mode === 'signal') {
    return null; // No numbers in signal mode
  }
  
  if (mode === 'evidence') {
    // Soft numbers with ~ prefix
    if (type === 'percent') {
      return `~${Math.round(value)}%`;
    }
    if (type === 'currency') {
      return `~${Math.round(value).toLocaleString()} EUR`;
    }
    return `~${Math.round(value).toLocaleString()}`;
  }
  
  // Decision mode: exact numbers
  if (type === 'percent') {
    return `${value.toFixed(1)}%`;
  }
  if (type === 'currency') {
    return `${value.toLocaleString()} EUR`;
  }
  return value.toLocaleString();
};

/**
 * Get detection label based on confidence mode
 * @param {string} mode - 'signal' | 'evidence' | 'decision'
 * @param {boolean} hasMissingFish - Whether missing fish detected
 * @param {string} growthDir - Growth direction
 * @returns {string}
 */
const getDetectionLabelByMode = (mode, hasMissingFish, growthDir) => {
  if (mode === 'signal') {
    return 'Deviation Signal';
  }
  if (mode === 'evidence') {
    if (hasMissingFish && growthDir === 'above') {
      return 'Probable Deviation';
    }
    return 'Deviation Detected';
  }
  // Decision mode: can use "Missing Fish"
  if (hasMissingFish && growthDir === 'above') {
    return 'Missing Fish';
  }
  if (growthDir === 'below') {
    return 'Below Model Performance';
  }
  if (growthDir === 'above') {
    return 'Above Model Performance';
  }
  return 'Normal';
};

/**
 * Get strength label based on confidence mode
 * @param {string} mode - 'signal' | 'evidence' | 'decision'
 * @param {string} rawStrength - 'High' | 'Moderate' | 'Low'
 * @returns {string}
 */
const getStrengthLabelByMode = (mode, rawStrength) => {
  if (mode === 'signal') {
    return 'Indication Only';
  }
  if (mode === 'evidence') {
    return rawStrength === 'High' ? 'Moderate-High' : rawStrength;
  }
  return rawStrength;
};

// Canonical conflict message
const CONFLICT_MESSAGE = 'Conflicting signals: FCR indicates potential stock discrepancy, but growth data does not confirm. Confidence reduced — validation required.';

// Blocked text patterns by detection type
const BLOCKED_PATTERNS = {
  missing_fish: [
    /no missing fish/i,
    /none detected/i,
    /no stock discrepancy/i,
    /stock integrity appears normal/i,
    /no evidence of missing/i,
  ],
  normal: [
    /missing fish estimate/i,
    /stock discrepancy/i,
    /unaccounted.*fish/i,
    /biomass deviation/i,
    /missing fish confirmed/i,
  ],
  conflict: [
    /missing fish confirmed/i,
    /no missing fish/i,
    /stock integrity appears normal/i,
  ],
};

/**
 * Check if text contains blocked patterns
 */
const containsBlockedPattern = (text, patterns) => {
  if (!text || typeof text !== 'string') return false;
  return patterns.some(pattern => pattern.test(text));
};

/**
 * applySSOTOverride
 * 
 * Single enforcement function that overrides ALL local narrative text
 * with DecisionContext canonical values.
 * 
 * @param {Object} output - The raw output object with local narrative fields
 * @param {Object} decisionContext - The DecisionContext from SSOT
 * @param {Object} ssot - The SSOT object containing conclusion and flags
 * @returns {Object} - Overridden output with all narrative from DecisionContext
 */
export const applySSOTOverride = (output, decisionContext, ssot) => {
  if (!output) return output;
  
  const conclusion = ssot?.conclusion || {};
  const hasConflict = ssot?.hasConflict || conclusion?.hasConflict || false;
  const wastedFeedKg = ssot?.wastedFeedKg || conclusion?.wastedFeedKg || 0;
  const detectionType = conclusion?.type || 'normal';
  
  // Create overridden output
  const overridden = { ...output };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 1: REMOVE local narrative fields (replace with null to force override)
  // ═══════════════════════════════════════════════════════════════════════════
  const localFieldsToRemove = [
    'localDetection',
    'localInterpretation',
    'localGrowthInsight',
    'localJumpStatus',
    'localFeeding',
    'localFeedingExplanation',
    'localValidation',
    'componentConclusion',
  ];
  
  localFieldsToRemove.forEach(field => {
    if (overridden[field] !== undefined) {
      overridden[field] = null; // Mark as removed
    }
  });
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 2: REPLACE with DecisionContext values
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Detection
  overridden.detection = decisionContext?.detection?.type || conclusion?.type || overridden.detection;
  overridden.detectionLabel = decisionContext?.detection?.label || conclusion?.confidenceLabel || 'Unknown';
  overridden.finalDetectionLabel = conclusion?.statement || decisionContext?.detection?.explanation || null;
  
  // Interpretation
  overridden.interpretation = conclusion?.interpretation || decisionContext?.interpretationMessage || null;
  overridden.finalInterpretation = conclusion?.interpretation || null;
  
  // Growth Insight
  overridden.growthInsight = conclusion?.growthInsight || ssot?.jumpStatusMessage || null;
  overridden.finalGrowthInsight = conclusion?.growthInsight || null;
  
  // Jump Status
  overridden.jumpStatus = ssot?.jumpStatusMessage || conclusion?.jumpStatusMessage || null;
  overridden.finalJumpStatus = ssot?.jumpStatusMessage || null;
  
  // Feeding Classification
  overridden.feeding = ssot?.feedingState || conclusion?.feedingState || null;
  overridden.finalFeedingClassification = ssot?.feedingState || conclusion?.feedingState || null;
  
  // Feeding Explanation
  overridden.feedingExplanation = ssot?.feedingNote || ssot?.feedingImpact || null;
  overridden.finalFeedingExplanation = ssot?.feedingNote || ssot?.feedingImpact || null;
  
  // Validation
  overridden.validation = decisionContext?.validation?.required ? 
    (decisionContext.validation.message || `Confirm via: ${(decisionContext.validation.examples || []).join(' · ')}`) : null;
  overridden.finalValidationMessage = overridden.validation;
  
  // Detection Explanation (signal hierarchy)
  overridden.detectionExplanation = conclusion?.detectionExplanation || null;
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 2b: PRECOMPUTE all child component narrative fields
  // Child components must render ONLY these fields — NO local branching
  // ═══════════════════════════════════════════════════════════════════════════
  
  const detectionStrength = ssot?.detectionStrength || 'Low';
  const hasMissingFish = ssot?.hasMissingFish || false;
  const primaryMethod = ssot?.primaryMethod || 'fcr';
  const missingFishCount = ssot?.missingFishCount || 0;
  const missingFishPercent = ssot?.missingFishPercent || 0;
  
  // ═══════════════════════════════════════════════════════════════════════════
  // GROWTH DIRECTION GATE — controls ALL missing-fish narrative display
  // Rule: Missing fish wording ONLY shown when growth_direction === 'above'
  // Rule: Conflict messages ONLY shown when growth_direction === 'above'
  // FCR raw numbers stay visible; interpretation is suppressed otherwise.
  // ═══════════════════════════════════════════════════════════════════════════
  const growthDir = output.sharedMetrics?.growth_direction || 'within';
  const showMissingFishNarrative = hasMissingFish && growthDir === 'above';
  const showConflict = hasConflict && growthDir === 'above';
  
  // ═══════════════════════════════════════════════════════════════════════════
  // SNAPSHOT MODE — single data point = warning signal only, not a conclusion
  // When true, ALL narrative must use conservative "indication only" wording.
  // Numbers are kept but described as single-point estimates.
  // EXCEPTION: Spreadsheet analysis with feed data is NEVER snapshot mode,
  // even with 1 sample — it has daily feed records and time-series data.
  // ═══════════════════════════════════════════════════════════════════════════
  const totalSamples = output.stats?.totalSamples || output.dataQuality?.details?.totalSamples || 0;
  const hasFeedData = !!(output.sharedMetrics?.reported_fcr || output.sharedMetrics?.model_fcr || ssot?.wastedFeedKg > 0 || output.dataQuality);
  const isSnapshotMode = totalSamples <= 1 && !hasFeedData;
  
  // ═══════════════════════════════════════════════════════════════════════════
  // GLOBAL CONFIDENCE MODE — gates ALL outputs based on detection strength
  // Snapshot mode ALWAYS forces 'signal' mode regardless of score
  // ═══════════════════════════════════════════════════════════════════════════
  const detectionStrengthScore = ssot?.detectionStrengthScore ?? 0;
  const confidenceMode = resolveConfidenceMode(detectionStrengthScore, isSnapshotMode);
  
  // Store for downstream use
  overridden._confidenceMode = confidenceMode;
  overridden._detectionStrengthScore = detectionStrengthScore;
  
  // ═══════════════════════════════════════════════════════════════════════════
  // MODE-GATED OUTPUTS — apply confidence mode to all display values
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Detection label — mode-aware
  const modeDetectionLabel = getDetectionLabelByMode(confidenceMode, hasMissingFish, growthDir);
  
  // Strength label — mode-aware
  const modeStrengthLabel = getStrengthLabelByMode(confidenceMode, detectionStrength);
  
  // Numbers — mode-gated
  const displayMissingFishCount = formatByMode(missingFishCount, confidenceMode, 'count');
  const displayMissingFishPercent = formatByMode(missingFishPercent, confidenceMode, 'percent');
  const displayFinancialImpact = formatByMode(ssot?.financialImpactEur || 0, confidenceMode, 'currency');
  const displayWastedFeed = formatByMode(wastedFeedKg, confidenceMode, 'count');
  
  // "Missing fish" wording only allowed in decision mode
  const canUseMissingFishWording = confidenceMode === 'decision';
  
  // Financial impact visibility
  const showFinancialImpact = confidenceMode === 'decision' || confidenceMode === 'evidence';
  const financialIsIndicative = confidenceMode === 'evidence';
  
  // FCR Evidence Label — mode-aware
  if (showMissingFishNarrative) {
    if (confidenceMode === 'signal') {
      overridden.finalEvidenceLabel = 'FCR Deviation: Indication Only';
    } else if (confidenceMode === 'evidence') {
      overridden.finalEvidenceLabel = `FCR Deviation: ${modeStrengthLabel} (indicative)`;
    } else {
      overridden.finalEvidenceLabel = `FCR Deviation: ${detectionStrength}`;
    }
  } else {
    overridden.finalEvidenceLabel = null;
  }
  
  // FCR Evidence Text — REMOVED (metric-only; no narrative in FCR banner)
  overridden.finalEvidenceText = null;
  
  // Missing Fish Assessment (for GrowthIntelligenceSummary row) — MODE-AWARE
  if (showMissingFishNarrative) {
    if (confidenceMode === 'signal') {
      // Signal mode: neutral deviation only, no "missing fish" or numbers
      overridden.finalMissingFishAssessment = 'Deviation detected — validation required';
    } else if (confidenceMode === 'evidence') {
      // Evidence mode: soft wording, indicative
      if (displayMissingFishCount) {
        overridden.finalMissingFishAssessment = `Probable deviation of ${displayMissingFishCount} fish (indicative) — validation recommended`;
      } else {
        overridden.finalMissingFishAssessment = 'Probable stock deviation detected — validation recommended';
      }
    } else {
      // Decision mode: can use "missing fish" wording with exact numbers
      if (primaryMethod === 'jump') {
        overridden.finalMissingFishAssessment = `Missing fish confirmed: ${displayMissingFishCount || missingFishCount.toLocaleString()} fish via sample jump`;
      } else if (detectionStrength === 'High') {
        overridden.finalMissingFishAssessment = `Missing fish: ${displayMissingFishCount || missingFishCount.toLocaleString()} (${displayMissingFishPercent || missingFishPercent.toFixed(1) + '%'}) — stock count review required`;
      } else if (detectionStrength === 'Moderate') {
        overridden.finalMissingFishAssessment = `Missing fish indicated: ${displayMissingFishCount || missingFishCount.toLocaleString()} — stock count review required`;
      } else {
        overridden.finalMissingFishAssessment = `Possible missing fish: ${displayMissingFishCount || missingFishCount.toLocaleString()} — continue monitoring`;
      }
    }
  } else if (hasMissingFish && growthDir === 'below') {
    // Growth is below model but hasMissingFish flag is set - this is a signal-only state
    overridden.finalMissingFishAssessment = 'Growth below model expectations. Continue monitoring for stock validation.';
  } else if (growthDir === 'below') {
    // Growth below model without missing fish flag
    overridden.finalMissingFishAssessment = confidenceMode === 'signal' 
      ? 'Growth tracking below model — monitoring recommended'
      : 'Growth below model — no stock adjustment indicated';
  } else {
    // Normal growth within model expectations
    overridden.finalMissingFishAssessment = confidenceMode === 'signal' 
      ? 'Growth within expected range — continue routine monitoring'
      : 'Normal growth pattern — no stock adjustment needed';
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 2b-2: DETECTION BLOCK CANONICAL FIELDS — MODE-AWARE
  // ═══════════════════════════════════════════════════════════════════════════
  
  // ── Detection Label (mode-aware) ──
  const growthDirConc = ssot?.conclusion?.growthDirection || output.sharedMetrics?.growth_direction || 'within';
  overridden.finalDetectionLabel = modeDetectionLabel;
  
  // ── Detection Strength (mode-aware) ──
  overridden.finalDetectionStrengthLabel = modeStrengthLabel;
  
  // ── Detection Summary (mode-aware) ──
  if (showMissingFishNarrative && missingFishCount > 0) {
    if (confidenceMode === 'signal') {
      overridden.finalDetectionSummary = 'Deviation signal detected. Validation required before interpretation.';
    } else if (confidenceMode === 'evidence') {
      overridden.finalDetectionSummary = `Probable deviation: ${displayMissingFishCount} fish (${displayMissingFishPercent}) — indicative estimate.`;
    } else {
      overridden.finalDetectionSummary = `${detectionStrength} confidence. ${missingFishCount.toLocaleString()} fish (${missingFishPercent.toFixed(1)}%) unaccounted.`;
    }
  } else if (growthDir === 'below') {
    overridden.finalDetectionSummary = 'Growth below model expectations. No confirmed stock discrepancy.';
  } else if (growthDir === 'above') {
    overridden.finalDetectionSummary = confidenceMode === 'signal' 
      ? 'Growth above model — deviation signal only.'
      : 'Growth above model. Monitoring recommended.';
  } else {
    overridden.finalDetectionSummary = 'Stock performance within expected parameters.';
  }
  
  // ── Trend Classification Label (replaces local trendClassification) ──
  if (growthDir === 'above') {
    if (showMissingFishNarrative) {
      overridden.finalTrendClassificationLabel = 'Above model — consistent with reduced stock';
    } else {
      overridden.finalTrendClassificationLabel = 'Above model expectations';
    }
  } else if (growthDir === 'below') {
    overridden.finalTrendClassificationLabel = 'Below model expectations';
  } else {
    overridden.finalTrendClassificationLabel = 'In line with model';
  }
  
  // ── Confidence Label (mode-aware) ──
  if (confidenceMode === 'signal') {
    overridden.finalConfidenceLabel = 'Signal Only';
  } else if (confidenceMode === 'evidence') {
    overridden.finalConfidenceLabel = 'Indicative';
  } else {
    overridden.finalConfidenceLabel = detectionStrengthScore >= 70 ? 'High' : 'Moderate';
  }
  
  // ── Explanation List (mode-aware) ──
  overridden.finalExplanationList = [];
  
  if (confidenceMode === 'signal') {
    // Signal mode: neutral only, no specific numbers or conclusions
    overridden.finalExplanationList = [
      { rank: 1, text: 'Deviation signal detected — requires validation with additional data', priority: 'medium' },
      { rank: 2, text: 'Signal strength insufficient for operational conclusions', priority: 'info' },
    ];
  } else if (confidenceMode === 'evidence') {
    // Evidence mode: soft wording
    if (showMissingFishNarrative && missingFishCount > 0) {
      overridden.finalExplanationList = [
        { rank: 1, text: `Probable stock deviation (${displayMissingFishCount}) — indicative estimate`, priority: 'medium' },
        { rank: 2, text: 'Validation recommended before operational decisions', priority: 'medium' },
      ];
    } else {
      overridden.finalExplanationList = [
        { rank: 1, text: 'Deviation pattern detected — requires validation', priority: 'medium' },
        { rank: 2, text: 'Additional samples recommended for confirmation', priority: 'info' },
      ];
    }
  } else {
    // Decision mode: full explanations with "missing fish" wording allowed
    if (showMissingFishNarrative && missingFishCount > 0) {
      if (detectionStrength === 'High') {
        overridden.finalExplanationList = [
          { rank: 1, text: `Missing fish confirmed: ${displayMissingFishCount} (${displayMissingFishPercent}) — high confidence`, priority: 'high' },
          { rank: 2, text: 'Stock count adjustment recommended for planning', priority: 'high' },
        ];
      } else {
        overridden.finalExplanationList = [
          { rank: 1, text: `Missing fish indicated: ${displayMissingFishCount} — stock count review required`, priority: 'high' },
          { rank: 2, text: 'Growth pattern consistent with reduced fish count', priority: 'medium' },
        ];
      }
    } else if (growthDir === 'below') {
      overridden.finalExplanationList = [
        { rank: 1, text: 'Growth below expectations — review feeding or conditions', priority: 'high' },
        { rank: 2, text: 'Environmental factors may be limiting growth', priority: 'medium' },
      ];
    } else if (growthDir === 'above' && !showMissingFishNarrative) {
      overridden.finalExplanationList = [
        { rank: 1, text: 'Growth exceeding model — no stock discrepancy detected', priority: 'info' },
        { rank: 2, text: 'Favorable conditions or genetics', priority: 'info' },
      ];
    } else {
      overridden.finalExplanationList = [
        { rank: 1, text: 'Stock tracking as expected', priority: 'info' },
      ];
    }
  }
  
  // ── Key Insight (mode-aware) ──
  if (confidenceMode === 'signal') {
    if (showMissingFishNarrative) {
      overridden.finalKeyInsight = 'Deviation signal detected — validation required before interpretation';
    } else if (growthDir !== 'within') {
      overridden.finalKeyInsight = `Growth ${growthDir === 'above' ? 'above' : 'below'} model — signal only`;
    } else {
      overridden.finalKeyInsight = null;
    }
  } else if (confidenceMode === 'evidence') {
    if (showMissingFishNarrative && missingFishCount > 0) {
      overridden.finalKeyInsight = `Probable deviation: ${displayMissingFishCount} fish (${displayMissingFishPercent}) — indicative`;
    } else if (growthDir === 'below') {
      overridden.finalKeyInsight = 'Growth below model — indicative signal';
    } else if (growthDir === 'above') {
      overridden.finalKeyInsight = 'Growth above model — indicative signal';
    } else {
      overridden.finalKeyInsight = null;
    }
  } else {
    // Decision mode: full insight with exact numbers
    if (showMissingFishNarrative && missingFishCount > 0) {
      overridden.finalKeyInsight = `Missing fish: ${displayMissingFishCount} (${displayMissingFishPercent}) — ${detectionStrength.toLowerCase()} confidence`;
    } else if (growthDir === 'below') {
      overridden.finalKeyInsight = 'Growth below model expectations. No confirmed stock discrepancy.';
    } else if (growthDir === 'above') {
      overridden.finalKeyInsight = 'Growth above model — monitoring recommended';
    } else {
      overridden.finalKeyInsight = null;
    }
  }
  
  // Top-Level Signal Message — mode-aware
  if (confidenceMode === 'signal') {
    overridden.finalTopLevelSignalMessage = showMissingFishNarrative ? 'Deviation: Signal Only' : null;
  } else if (confidenceMode === 'evidence') {
    overridden.finalTopLevelSignalMessage = showMissingFishNarrative ? `FCR: ${modeStrengthLabel} (indicative)` : null;
  } else {
    overridden.finalTopLevelSignalMessage = showMissingFishNarrative ? `FCR: ${detectionStrength}` : null;
  }
  
  // Conflict message (explicit field for child components)
  overridden.finalConflictMessage = null; // Will be set in STEP 3 if conflict exists
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 2c: GROWTH VS MODEL / GROWTH PROGNOSIS precomputed fields
  // These fields enforce narrative consistency across all Growth panels
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Extract raw data for processing (NOT for direct rendering)
  const sharedMetrics = output.sharedMetrics || {};
  const analyticalSignals = output.analyticalSignals || {};
  const samplingRec = output.samplingRecommendation || {};
  const growthFit = output.growthFit || {};
  const trajectoryJump = output.trajectoryJump || {};
  
  // Decision class from decisionContext (drives severity enforcement)
  const decisionClass = decisionContext?.decisionClass || 'MONITOR_ONLY';
  const isMonitorOnly = decisionClass === 'MONITOR_ONLY';
  const isValidationRequired = decisionClass === 'VALIDATION_REQUIRED';
  const isSuitableForPlanning = decisionClass === 'ACTIONABLE';
  
  overridden.decisionClass = decisionClass;
  
  // ── Growth Direction ──
  const growthDirection = sharedMetrics.growth_direction || 'within';
  const deviationPercent = sharedMetrics.deviation_percent;
  
  if (growthDirection === 'above') {
    overridden.finalGrowthDirectionLabel = 'Above Model';
    if (showMissingFishNarrative && !isMonitorOnly) {
      overridden.finalGrowthDirectionDescription = 'Growth exceeds model expectations. This pattern is consistent with reduced fish count.';
    } else if (isMonitorOnly) {
      overridden.finalGrowthDirectionDescription = 'Growth exceeds model expectations. Continue monitoring.';
    } else {
      overridden.finalGrowthDirectionDescription = 'Growth exceeds model expectations.';
    }
  } else if (growthDirection === 'below') {
    overridden.finalGrowthDirectionLabel = 'Below Model';
    overridden.finalGrowthDirectionDescription = 'Below model. Review environmental conditions and feed strategy.';
  } else {
    overridden.finalGrowthDirectionLabel = 'Within Model';
    overridden.finalGrowthDirectionDescription = 'Growth trajectory is within expected parameters.';
  }
  
  // Growth Direction warning (only when NOT monitor-only and conditions warrant)
  if (growthDirection === 'within' && showMissingFishNarrative && primaryMethod === 'fcr' && !isMonitorOnly) {
    overridden.finalGrowthDirectionWarning = 'Growth consistency may be due to reduced fish count rather than true performance.';
  } else {
    overridden.finalGrowthDirectionWarning = null;
  }
  
  // ── Growth Pattern Classification ──
  const rawPattern = analyticalSignals.pattern || {};
  const rawPatternName = (rawPattern.pattern || '').toLowerCase();
  
  // DECISION-CLASS ENFORCEMENT: Block urgent/severe language in MONITOR_ONLY
  if (isMonitorOnly) {
    // Downgrade pattern to neutral language
    if (rawPatternName.includes('sudden upward shift')) {
      overridden.finalGrowthPatternLabel = 'Deviation detected';
      overridden.finalGrowthPatternDescription = 'A deviation from the growth model was observed. Continue monitoring to confirm trend.';
    } else if (rawPatternName.includes('divergence')) {
      overridden.finalGrowthPatternLabel = 'Gradual deviation';
      overridden.finalGrowthPatternDescription = 'Growth is deviating from model over time. Monitor for confirmation.';
    } else {
      overridden.finalGrowthPatternLabel = rawPattern.pattern || 'Normal pattern';
      overridden.finalGrowthPatternDescription = rawPattern.description?.replace(/discrete loss event/gi, 'potential deviation').replace(/fish loss/gi, 'deviation') || 'Growth pattern within normal variation.';
    }
  } else if (isValidationRequired || showConflict) {
    if (rawPatternName.includes('sudden upward shift')) {
      overridden.finalGrowthPatternLabel = 'Sudden upward shift — validation required';
      overridden.finalGrowthPatternDescription = 'A significant deviation was detected. Validation required before drawing conclusions.';
    } else {
      overridden.finalGrowthPatternLabel = rawPattern.pattern ? `${rawPattern.pattern} — validation required` : 'Pattern — validation required';
      overridden.finalGrowthPatternDescription = null; // Conflict badge handles messaging
    }
  } else {
    // Suitable for planning: use pattern as-is but still avoid raw "discrete loss event" unless High confidence
    if (detectionStrength === 'High' && rawPatternName.includes('sudden upward shift')) {
      overridden.finalGrowthPatternLabel = 'Sudden upward shift';
      overridden.finalGrowthPatternDescription = 'A significant upward deviation jump was detected, consistent with a discrete reduction in fish count.';
    } else {
      overridden.finalGrowthPatternLabel = rawPattern.pattern || 'Normal pattern';
      overridden.finalGrowthPatternDescription = rawPattern.description?.replace(/discrete loss event/gi, 'potential stock discrepancy') || 'Growth pattern within normal variation.';
    }
  }
  
  // ── Growth Signal Strength ──
  const rawSignalStrength = analyticalSignals.signalStrength || {};
  const rawStrengthLabel = rawSignalStrength.strength || 'Low';
  const rawStrengthScore = rawSignalStrength.score || 0;
  
  // DECISION-CLASS ENFORCEMENT: Cap signal strength label for MONITOR_ONLY
  if (isMonitorOnly && (rawStrengthLabel === 'Strong' || rawStrengthLabel === 'High')) {
    overridden.finalGrowthSignalStrengthLabel = 'Moderate';
    overridden.finalGrowthSignalStrengthScore = Math.min(rawStrengthScore, 50);
  } else if (showConflict) {
    overridden.finalGrowthSignalStrengthLabel = 'Conflicting';
    overridden.finalGrowthSignalStrengthScore = rawStrengthScore;
  } else {
    overridden.finalGrowthSignalStrengthLabel = rawStrengthLabel;
    overridden.finalGrowthSignalStrengthScore = rawStrengthScore;
  }
  
  // ── Growth-Fit Explanation & Assessment ──
  const growthFitDetected = growthFit.detected || false;
  const growthFitExplanation = growthFit.explanation || '';
  
  if (growthFitDetected) {
    if (isMonitorOnly) {
      // MONITOR_ONLY: Present as supporting signal only
      overridden.finalGrowthFitExplanation = 'Growth-curve fitting provides a supporting analytical signal. Continue monitoring to confirm trend before acting on this estimate.';
      overridden.finalGrowthFitAssessment = 'Supporting signal — monitor only';
      overridden.finalGrowthFitVisibility = 'supporting';
    } else if (isValidationRequired || showConflict) {
      overridden.finalGrowthFitExplanation = 'Growth-curve fitting suggests a potential discrepancy. Validation required before using this estimate for operational decisions.';
      overridden.finalGrowthFitAssessment = 'Indicative — validation required';
      overridden.finalGrowthFitVisibility = 'indicative';
    } else if (isSuitableForPlanning && detectionStrength === 'High') {
      overridden.finalGrowthFitExplanation = growthFitExplanation.replace(/may be missing/gi, 'are likely unaccounted for') || 'Growth-curve analysis supports the missing fish estimate.';
      overridden.finalGrowthFitAssessment = 'High confidence estimate';
      overridden.finalGrowthFitVisibility = 'primary';
    } else {
      overridden.finalGrowthFitExplanation = growthFitExplanation.replace(/suggests approximately/gi, 'indicates approximately') || 'Growth-curve fitting provides an estimate.';
      overridden.finalGrowthFitAssessment = 'Moderate confidence estimate';
      overridden.finalGrowthFitVisibility = 'primary';
    }
  } else {
    overridden.finalGrowthFitExplanation = null;
    overridden.finalGrowthFitAssessment = null;
    overridden.finalGrowthFitVisibility = 'hidden';
  }
  
  // ── Sampling Recommendation ──
  const rawSamplingRec = samplingRec.recommendation || '';
  const rawSamplingConfidence = samplingRec.confidenceLevel || 'Low';
  const rawSamplingPriority = samplingRec.priority || 'normal';
  
  // DECISION-CLASS ENFORCEMENT: No urgent recommendations in MONITOR_ONLY
  if (isMonitorOnly) {
    if (rawSamplingRec.toLowerCase().includes('immediate')) {
      overridden.finalSamplingRecommendation = 'Schedule follow-up sampling within normal cycle to confirm observations.';
    } else {
      overridden.finalSamplingRecommendation = rawSamplingRec || 'Continue routine sampling schedule.';
    }
    overridden.finalSamplingConfidence = rawSamplingConfidence === 'High' ? 'Moderate' : rawSamplingConfidence;
    overridden.finalSamplingPriority = 'normal';
  } else if (showConflict) {
    overridden.finalSamplingRecommendation = 'Re-sample recommended to resolve signal conflict before operational decisions.';
    overridden.finalSamplingConfidence = 'Required';
    overridden.finalSamplingPriority = 'urgent';
  } else if (isValidationRequired) {
    overridden.finalSamplingRecommendation = rawSamplingRec || 'Validation sampling required before operational changes.';
    overridden.finalSamplingConfidence = rawSamplingConfidence;
    overridden.finalSamplingPriority = rawSamplingPriority;
  } else {
    overridden.finalSamplingRecommendation = rawSamplingRec || 'Continue routine sampling schedule.';
    overridden.finalSamplingConfidence = rawSamplingConfidence;
    overridden.finalSamplingPriority = rawSamplingPriority;
  }
  
  // ── Data Quality Label ──
  const dataQualityScore = output.dataQuality?.score ?? 50;
  if (dataQualityScore >= 70) {
    overridden.finalDataQualityLabel = 'Good';
  } else if (dataQualityScore >= 40) {
    overridden.finalDataQualityLabel = 'Fair';
  } else {
    overridden.finalDataQualityLabel = 'Limited';
  }
  overridden.finalDataQualityScore = dataQualityScore;
  
  // ── Adjusted Fish Count Label ──
  if (ssot?.adjustedFishCount != null && ssot.adjustedFishCount !== output.reportedFishNumber) {
    overridden.finalAdjustedFishCountLabel = ssot.adjustedFishCount.toLocaleString();
  } else {
    overridden.finalAdjustedFishCountLabel = 'No adjustment applied';
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 2d: ADDITIONAL CANONICAL NARRATIVE FIELDS (NO BLANKS ALLOWED)
  // ═══════════════════════════════════════════════════════════════════════════
  
  // ── Growth Summary Notice (replaces "Growth deviation detected") ──
  if (growthDirection === 'above' && showMissingFishNarrative) {
    overridden.finalGrowthSummaryNotice = 'Growth exceeds model — consistent with reduced stock count';
  } else if (growthDirection === 'above') {
    overridden.finalGrowthSummaryNotice = 'Growth exceeds model expectations';
  } else if (growthDirection === 'below') {
    overridden.finalGrowthSummaryNotice = 'Below model';
  } else {
    overridden.finalGrowthSummaryNotice = null; // No notice needed for within-model
  }
  
  // ── Suspect Sample Warning ──
  const suspectSamples = trajectoryJump?.suspect_samples || [];
  if (suspectSamples.length > 0 && !showMissingFishNarrative) {
    overridden.finalSuspectSampleWarning = 'Sample flagged for review — exclude to see underlying trend';
  } else if (suspectSamples.length > 0 && showMissingFishNarrative) {
    overridden.finalSuspectSampleWarning = 'Sample contributed to detection — review for confirmation';
  } else {
    overridden.finalSuspectSampleWarning = null;
  }
  
  // ── Biomass Density Summary (for Advanced Signals) ──
  const biomassDensity = analyticalSignals.biomassDensity || {};
  if (biomassDensity.available && biomassDensity.interpretation) {
    if (isMonitorOnly) {
      overridden.finalBiomassDensitySummary = 'Biomass density analysis provides supporting context. Continue monitoring.';
    } else if (showConflict || isValidationRequired) {
      overridden.finalBiomassDensitySummary = 'Biomass density analysis requires validation before interpretation.';
    } else {
      overridden.finalBiomassDensitySummary = biomassDensity.interpretation.replace(/discrete loss event/gi, 'potential stock discrepancy').replace(/fish loss/gi, 'stock discrepancy');
    }
  } else {
    overridden.finalBiomassDensitySummary = null;
  }
  
  // ── Incorrect Sample Summary (for Advanced Signals) ──
  const incorrectSamples = analyticalSignals.incorrectSamples || output.incorrectSamples || {};
  if (incorrectSamples.detected && incorrectSamples.count > 0) {
    if (isMonitorOnly) {
      overridden.finalIncorrectSampleSummary = `${incorrectSamples.count} sample(s) flagged for potential measurement error. Review before drawing conclusions.`;
    } else {
      overridden.finalIncorrectSampleSummary = incorrectSamples.summary?.replace(/likely incorrect/gi, 'flagged for review') || `${incorrectSamples.count} sample(s) flagged for review.`;
    }
    overridden.finalIncorrectSampleExplanation = 'Exclude flagged samples to see underlying growth trend.';
  } else {
    overridden.finalIncorrectSampleSummary = null;
    overridden.finalIncorrectSampleExplanation = null;
  }
  
  // ── Historical Deviation Summary (for Advanced Signals) ──
  const divergence = analyticalSignals.divergence || {};
  const strongestWindow = analyticalSignals.strongestWindow || {};
  if (divergence.detected || strongestWindow.avgDeviation) {
    if (isMonitorOnly) {
      overridden.finalHistoricalDeviationSummary = 'Historical deviation analysis provides context. Continue monitoring for trend confirmation.';
    } else if (showConflict || isValidationRequired) {
      overridden.finalHistoricalDeviationSummary = null; // Section heading conveys context
    } else if (showMissingFishNarrative && detectionStrength === 'High') {
      overridden.finalHistoricalDeviationSummary = 'Persistent deviation pattern supports missing fish estimate.';
    } else {
      overridden.finalHistoricalDeviationSummary = 'Deviation pattern detected in historical data.';
    }
  } else {
    overridden.finalHistoricalDeviationSummary = null;
  }
  
  // ── Advanced Signals Summary (consolidated for the entire section) ──
  if (isMonitorOnly) {
    overridden.finalAdvancedSignalsSummary = 'Advanced analytical signals provide supporting context only. Continue monitoring.';
  } else if (showConflict || isValidationRequired) {
    overridden.finalAdvancedSignalsSummary = 'Advanced signals require validation before interpretation.';
  } else if (showMissingFishNarrative && detectionStrength === 'High') {
    overridden.finalAdvancedSignalsSummary = 'Advanced signals support the missing fish estimate with high confidence.';
  } else if (showMissingFishNarrative) {
    overridden.finalAdvancedSignalsSummary = 'Advanced signals provide supporting evidence.';
  } else {
    overridden.finalAdvancedSignalsSummary = null;
  }
  
  // ── Detection Strength (canonical, single source) ──
  // This ensures detection strength is consistent across ALL panels
  overridden.finalDetectionStrength = detectionStrength;
  overridden.finalDetectionStrengthScore = ssot?.detectionStrengthScore ?? 0;
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 3: If hasSignalConflict = true AND growth is above, FORCE conflict message
  // When growth is NOT above model, suppress conflict display entirely (Rule 3)
  // ═══════════════════════════════════════════════════════════════════════════
  if (showConflict) {
    overridden.finalDetectionLabel = CONFLICT_MESSAGE;
    overridden.finalInterpretation = CONFLICT_MESSAGE;
    overridden.interpretation = CONFLICT_MESSAGE;
    overridden.conflictMessage = CONFLICT_MESSAGE;
    overridden.hasConflict = true;
    
    // ★ OVERRIDE all child component narrative fields for conflict case
    overridden.finalEvidenceLabel = 'Signal Conflict — Validation Required';
    overridden.finalEvidenceText = CONFLICT_MESSAGE;
    overridden.finalMissingFishAssessment = 'Signal detected — validation required due to conflicting evidence';
    overridden.finalKeyInsight = CONFLICT_MESSAGE;
    overridden.finalConflictMessage = CONFLICT_MESSAGE;
    overridden.finalTopLevelSignalMessage = 'Signal detected — validation required due to conflicting evidence';
    
    // ★ OVERRIDE Growth vs Model / Growth Prognosis fields for conflict
    overridden.finalGrowthPatternLabel = 'Pattern conflict — validation required';
    overridden.finalGrowthPatternDescription = 'Growth pattern analysis shows conflicting signals. Validation required before interpretation.';
    overridden.finalGrowthSignalStrengthLabel = 'Conflicting';
    overridden.finalGrowthFitExplanation = 'Growth-fit estimate requires validation due to conflicting signals.';
    overridden.finalGrowthFitAssessment = 'Conflicting signals — validation required';
    overridden.finalGrowthFitVisibility = 'indicative';
    overridden.finalSamplingRecommendation = 'Re-sample recommended to resolve signal conflict before operational decisions.';
    overridden.finalSamplingConfidence = 'Required';
    overridden.finalSamplingPriority = 'urgent';
    overridden.finalGrowthDirectionWarning = 'Signal conflict detected — interpret growth direction with caution.';
    
    // ★ OVERRIDE new canonical fields for conflict
    overridden.finalGrowthSummaryNotice = 'Conflicting signals — interpretation requires validation';
    overridden.finalSuspectSampleWarning = 'Sample analysis affected by signal conflict — validation required';
    overridden.finalBiomassDensitySummary = 'Biomass density analysis requires validation due to conflicting signals.';
    overridden.finalIncorrectSampleSummary = overridden.finalIncorrectSampleSummary ? 'Flagged samples require validation due to signal conflict.' : null;
    overridden.finalHistoricalDeviationSummary = 'Historical deviation requires validation due to conflicting signals.';
    overridden.finalAdvancedSignalsSummary = 'Advanced signals require validation due to conflicting evidence.';
    overridden.finalDetectionStrength = 'Conflicting';
    
    // ★ OVERRIDE detection block fields for conflict
    overridden.finalDetectionLabel = 'Signal Conflict';
    overridden.finalDetectionStrengthLabel = 'Conflicting';
    overridden.finalDetectionSummary = 'Conflicting signals detected — validation required before interpretation.';
    overridden.finalTrendClassificationLabel = 'Conflicting signals — validation required';
    overridden.finalConfidenceLabel = 'Validation Required';
    overridden.finalExplanationList = [
      { rank: 1, text: 'Signal conflict — re-sample recommended before drawing conclusions', priority: 'high' },
    ];
    
    // Also update detection explanation to reflect conflict
    overridden.detectionExplanation = conclusion?.detectionExplanation || 
      'FCR-based signal is primary, but conflicts with growth data, reducing confidence';
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 4: Feeding classification corrections
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Rule 4: If feedingState === 'Restricted' AND wastedFeedKg > 0,
  // replace all feeding explanations — remove "feed waste" wording
  if (wastedFeedKg > 0 && (overridden.feeding === 'Restricted' || overridden.finalFeedingClassification === 'Restricted')) {
    overridden.feedingExplanation = 'Feeding below model overall, with some periods of overfeeding.';
    overridden.finalFeedingExplanation = overridden.feedingExplanation;
  }
  
  // Existing rule: If wastedFeedKg > 0 and feeding was "Optimal", reclassify to "Mixed"
  if (wastedFeedKg > 0) {
    if (overridden.feeding === 'Optimal' || overridden.finalFeedingClassification === 'Optimal') {
      overridden.feeding = 'Mixed';
      overridden.finalFeedingClassification = 'Mixed';
      overridden.feedingExplanation = 'Feeding broadly aligned overall, but overfeeding occurred during specific periods';
      overridden.finalFeedingExplanation = overridden.feedingExplanation;
    }
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 5: BLOCK contradictory text based on detection type
  // Use display-adjusted detection type: when growth doesn't confirm FCR,
  // treat as growth_suppression/normal for pattern blocking purposes
  // ═══════════════════════════════════════════════════════════════════════════
  const displayDetectionType = showMissingFishNarrative ? detectionType :
    (growthDir === 'below' ? 'growth_suppression' : 'normal');
  const patternsToBlock = showConflict ? BLOCKED_PATTERNS.conflict :
    displayDetectionType === 'missing_fish' ? BLOCKED_PATTERNS.missing_fish :
    displayDetectionType === 'normal' ? BLOCKED_PATTERNS.normal : [];
  
  // Check and nullify any fields containing blocked patterns
  const textFields = [
    'finalDetectionLabel',
    'finalInterpretation',
    'finalGrowthInsight',
    'finalJumpStatus',
    'interpretation',
    'growthInsight',
    'jumpStatus',
  ];
  
  // Don't block conflict message itself
  if (!showConflict) {
    textFields.forEach(field => {
      if (overridden[field] && containsBlockedPattern(overridden[field], patternsToBlock)) {
        // Replace with the calibrated message from the upstream conclusion
        // SSOT instead of hard-coding wording. `conclusion.statement`
        // already encodes the indicative-signal-aware copy
        // ("No confirmed missing fish signal detected. Growth is currently
        //  tracking close to the adjusted model …") when appropriate, so
        // honouring it here prevents this override layer from leaking the
        // old "Stock integrity appears normal" contradiction back into
        // the report.
        if (detectionType === 'missing_fish') {
          overridden[field] = conclusion?.statement || conclusion?.interpretation || overridden[field];
        } else if (detectionType === 'normal') {
          overridden[field] = conclusion?.statement
            || conclusion?.interpretation
            || 'Stock integrity appears normal. Growth trajectory is within expected parameters.';
        }
      }
    });
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 6: Ensure precision control values are used
  // ═══════════════════════════════════════════════════════════════════════════
  if (ssot?.precisionControlApplied) {
    overridden.missingFishCount = ssot.missingFishCount; // Already rounded
    overridden.missingFishPercent = ssot.missingFishPercent; // Already rounded
    overridden.precisionControlApplied = true;
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 7: GLOBAL CONFIDENCE MODE FINAL ENFORCEMENT
  // Applies mode-specific overrides for signal, evidence, and decision modes.
  // Snapshot mode is already handled by resolveConfidenceMode() → 'signal'
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Signal mode: no numbers, no €, neutral wording only
  if (confidenceMode === 'signal') {
    const SIGNAL_TAG = isSnapshotMode 
      ? 'Single data point — validation required'
      : 'Signal only — validation required';
    
    // Growth direction description — add signal qualifier
    if (overridden.finalGrowthDirectionDescription) {
      overridden.finalGrowthDirectionDescription = `${overridden.finalGrowthDirectionDescription} (${isSnapshotMode ? 'single data point' : 'signal only'})`;
    }
    
    // Growth summary notice — conservative
    overridden.finalGrowthSummaryNotice = overridden.finalGrowthSummaryNotice 
      ? `${overridden.finalGrowthSummaryNotice} (${isSnapshotMode ? 'single sample' : 'signal only'})` : null;
    
    // Trend classification — cannot establish trend in signal mode
    overridden.finalTrendClassificationLabel = isSnapshotMode 
      ? 'Single data point — no trend established'
      : 'Insufficient confidence — trend not confirmed';
    
    // Sampling recommendation — always recommend more data
    overridden.finalSamplingRecommendation = isSnapshotMode
      ? 'Additional samples required before any operational decision. A single data point provides indication only.'
      : 'Additional data required to confirm signal. Continue monitoring.';
    overridden.finalSamplingConfidence = 'Insufficient';
    overridden.finalSamplingPriority = 'urgent';
    
    // Growth fit — not applicable in signal mode
    overridden.finalGrowthFitAssessment = 'Not applicable — signal only';
    overridden.finalGrowthFitExplanation = 'Growth-curve fitting requires higher confidence data. Current result is a signal only.';
    overridden.finalGrowthFitVisibility = 'not_applicable';
    
    // Advanced signals — all suppressed in signal mode
    overridden.finalGrowthPatternLabel = 'Insufficient Data';
    overridden.finalGrowthPatternDescription = 'Pattern analysis requires higher confidence data.';
    overridden.finalGrowthSignalStrengthLabel = 'Indication Only';
    overridden.finalHistoricalDeviationSummary = null;
    overridden.finalBiomassDensitySummary = null;
    overridden.finalAdvancedSignalsSummary = `${SIGNAL_TAG}. Advanced signals are not reliable at this confidence level.`;
    
    // Financial context — never shown in signal mode
    overridden.financialConfidenceLabel = 'Not Actionable';
    
    // Conflict message — not applicable in signal mode
    overridden.finalConflictMessage = null;
    
    // Suspect sample warning — not applicable in signal mode
    overridden.finalSuspectSampleWarning = null;
  }
  
  // Evidence mode: soft numbers with ~, "indicative" wording
  if (confidenceMode === 'evidence') {
    // Growth direction description — add indicative qualifier
    if (overridden.finalGrowthDirectionDescription) {
      overridden.finalGrowthDirectionDescription = `${overridden.finalGrowthDirectionDescription} (indicative)`;
    }
    
    // Growth summary notice — indicative
    overridden.finalGrowthSummaryNotice = overridden.finalGrowthSummaryNotice 
      ? `${overridden.finalGrowthSummaryNotice} (indicative)` : null;
    
    // Trend classification — soft confidence
    if (showMissingFishNarrative) {
      overridden.finalTrendClassificationLabel = 'Probable deviation — indicative';
    }
    
    // Sampling — recommend validation
    overridden.finalSamplingRecommendation = overridden.finalSamplingRecommendation || 'Validation sampling recommended to confirm indicative findings.';
    overridden.finalSamplingConfidence = 'Indicative';
    
    // Growth fit — indicative
    if (overridden.finalGrowthFitAssessment && !overridden.finalGrowthFitAssessment.includes('indicative')) {
      overridden.finalGrowthFitAssessment = `${overridden.finalGrowthFitAssessment} (indicative)`;
    }
    
    // Financial context — indicative only
    overridden.financialConfidenceLabel = 'Indicative';
    
    // Advanced signals — soft wording
    if (overridden.finalAdvancedSignalsSummary) {
      overridden.finalAdvancedSignalsSummary = `${overridden.finalAdvancedSignalsSummary} (indicative)`;
    }
  }
  
  // Decision mode: exact numbers, direct wording, full financial impact
  // (Already set in main logic above — no additional overrides needed)

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 8: Add override metadata for validation
  // ═══════════════════════════════════════════════════════════════════════════
  overridden._ssotOverrideApplied = true;
  overridden._overrideTimestamp = new Date().toISOString();
  overridden._detectionType = detectionType;
  overridden._hasConflict = showConflict; // Display-level conflict (suppressed when growth not above)
  overridden._rawHasConflict = hasConflict; // Raw calculation conflict
  overridden._wastedFeedKg = wastedFeedKg;
  overridden._growthDirection = growthDir;
  overridden._showMissingFishNarrative = showMissingFishNarrative;
  overridden._isSnapshotMode = isSnapshotMode;
  overridden._totalSamples = totalSamples;
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 8: BUILD finalDecision — THE SINGLE RESOLVED DECISION OBJECT
  // Every component must be a dumb renderer of this object.
  // No component may derive interpretation from raw metrics.
  // ═══════════════════════════════════════════════════════════════════════════
  overridden.finalDecision = {
    // Growth
    growthDirectionLabel: overridden.finalGrowthDirectionLabel || null,
    growthDirectionState: growthDir || null,
    growthDirectionDescription: overridden.finalGrowthDirectionDescription || null,
    growthDirectionWarning: overridden.finalGrowthDirectionWarning || null,
    growthSummaryNotice: overridden.finalGrowthSummaryNotice || null,
    
    // Detection
    detectionLabel: overridden.finalDetectionLabel || null,
    detectionType: showMissingFishNarrative
      ? (ssot?.conclusion?.type || decisionContext?.detection?.type || null)
      : (growthDir === 'below' ? 'below_model' : 'normal'),
    detectionStrengthLabel: overridden.finalDetectionStrengthLabel || detectionStrength || null,
    detectionStrengthScore: overridden.finalDetectionStrengthScore ?? 0,
    detectionProbability: ssot?.conclusion?.probability ?? decisionContext?.detection?.probability ?? null,
    detectionSummary: overridden.finalDetectionSummary || null,
    confidenceLabel: overridden.finalConfidenceLabel || null,
    trendClassificationLabel: overridden.finalTrendClassificationLabel || null,
    
    // Missing fish
    missingFishAssessment: overridden.finalMissingFishAssessment || null,
    hasMissingFish: showMissingFishNarrative,
    missingFishUserVisible: showMissingFishNarrative && confidenceMode !== 'signal', // Hidden in signal mode
    // Mode-gated display values
    missingFishCount: showMissingFishNarrative ? (ssot?.missingFishCount || 0) : 0,
    missingFishPercent: showMissingFishNarrative ? (ssot?.missingFishPercent || 0) : 0,
    missingFishCountDisplay: confidenceMode === 'signal' ? null : displayMissingFishCount,
    missingFishPercentDisplay: confidenceMode === 'signal' ? null : displayMissingFishPercent,
    adjustedFishCountLabel: showMissingFishNarrative && confidenceMode !== 'signal' ? (overridden.finalAdjustedFishCountLabel || null) : null,
    
    // Key insight (single primary conclusion — no duplication)
    keyInsight: overridden.finalKeyInsight || null,
    topLevelSignalMessage: overridden.finalTopLevelSignalMessage || null,
    
    // Explanations / Analysis Summary
    explanationList: overridden.finalExplanationList || [],
    analysisSummary: overridden.finalExplanationList || [],
    
    // Interpretation & conflict
    interpretation: overridden.finalInterpretation || null,
    detectionExplanation: overridden.detectionExplanation || null,
    conflictMessage: overridden.finalConflictMessage || null,
    
    // Sampling
    samplingRecommendation: overridden.finalSamplingRecommendation || null,
    samplingConfidence: overridden.finalSamplingConfidence || null,
    samplingPriority: overridden.finalSamplingPriority || null,
    
    // Feeding impact
    feedingImpactLabel: overridden.finalFeedingClassification || null,
    feedingImpactRfi: ssot?.rfi ?? null,
    
    // Data quality
    dataQualityLabel: overridden.finalDataQualityLabel || null,
    dataQualityScore: overridden.finalDataQualityScore ?? null,
    
    // Financial context — mode-gated
    financialContextLabel: confidenceMode === 'signal' ? 'Not Actionable' : (confidenceMode === 'evidence' ? 'Indicative' : (showMissingFishNarrative ? (overridden.financialConfidenceLabel || null) : null)),
    financialContextLevel: confidenceMode === 'signal' ? 'not_actionable' : (confidenceMode === 'evidence' ? 'indicative' : (showMissingFishNarrative ? (ssot?.financialConfidenceLevel || decisionContext?.financialConfidence?.level || null) : 'not_actionable')),
    financialContextReason: confidenceMode === 'signal' 
      ? 'Signal mode: financial estimates not available. Validation required.' 
      : (confidenceMode === 'evidence' 
        ? 'Evidence mode: financial estimates are indicative only.'
        : (showMissingFishNarrative ? (ssot?.financialConfidenceReason || decisionContext?.financialConfidence?.reason || null) : 'No decision-grade financial estimate available for stock discrepancy.')),
    financialImpactDisplay: displayFinancialImpact,
    financialIsIndicative: financialIsIndicative,
    showFinancialImpact: showFinancialImpact && showMissingFishNarrative,
    
    // Stock integrity
    stockIntegrityLabel: null, // Numeric only — set by component from score
    
    // FCR evidence (metric-only, no rephrased conclusion)
    fcrEvidenceLabel: overridden.finalEvidenceLabel || null,
    
    // Growth fit
    growthFitAssessment: overridden.finalGrowthFitAssessment || null,
    growthFitExplanation: overridden.finalGrowthFitExplanation || null,
    growthFitVisibility: overridden.finalGrowthFitVisibility || null,
    
    // Advanced signals
    advancedSignals: {
      growthPatternLabel: overridden.finalGrowthPatternLabel || null,
      growthPatternText: overridden.finalGrowthPatternDescription || null,
      historicalDeviationText: overridden.finalHistoricalDeviationSummary || null,
      growthSignalStrengthLabel: overridden.finalGrowthSignalStrengthLabel || null,
      growthSignalStrengthScore: overridden.finalGrowthSignalStrengthScore ?? null,
      biomassDensitySummary: overridden.finalBiomassDensitySummary || null,
      incorrectSampleSummary: overridden.finalIncorrectSampleSummary || null,
      incorrectSampleExplanation: overridden.finalIncorrectSampleExplanation || null,
      advancedSignalsSummary: overridden.finalAdvancedSignalsSummary || null,
    },
    
    // Suspect samples
    suspectSampleWarning: overridden.finalSuspectSampleWarning || null,
    
    // Jump status (SSOT canonical)
    jumpStatus: overridden.finalJumpStatus || ssot?.jumpStatusMessage || null,
    
    // Actions (from decisionContext)
    actions: {
      primary: decisionContext?.primaryAction || null,
      secondary: decisionContext?.secondaryActions || [],
    },
    
    // Flags
    flags: {
      validationRequired: showConflict || confidenceMode !== 'decision' || false,
      hasConflict: showConflict || false,
      notDecisionGrade: confidenceMode !== 'decision',
      precisionControlApplied: ssot?.precisionControlApplied || false,
      isSnapshotMode: isSnapshotMode,
    },
    
    // Global Confidence Mode — gates ALL outputs
    confidenceMode: confidenceMode,
    confidenceModeLabel: confidenceMode === 'signal' ? 'Signal Only' : (confidenceMode === 'evidence' ? 'Indicative' : 'Decision Grade'),
    canUseMissingFishWording: canUseMissingFishWording,
    
    // Snapshot mode metadata
    isSnapshotMode: isSnapshotMode,
    totalSamples: totalSamples,
    snapshotWarning: isSnapshotMode ? 'Single data point — validation required' : null,
    lowSampleWarning: (!isSnapshotMode && totalSamples > 0 && totalSamples <= 3) 
      ? 'Based on available dataset — validation recommended if sample frequency is low' 
      : null,
    
    // Mode-specific UI labels — components use these for static text
    modeLabels: {
      estimatedMissingFishLabel: confidenceMode === 'signal' ? null : (confidenceMode === 'evidence' ? 'Probable Deviation' : 'Estimated Missing Fish'),
      missingFishAssessmentLabel: confidenceMode === 'signal' ? 'Deviation Assessment' : (confidenceMode === 'evidence' ? 'Deviation Assessment (Indicative)' : 'Missing Fish Assessment'),
      revenueRiskLabel: confidenceMode === 'signal' ? null : (confidenceMode === 'evidence' ? 'Revenue Impact (Indicative)' : 'Missing Fish Revenue Risk'),
      detectionReconciliationTitle: 'Detection Methods',
      reportTitle: confidenceMode === 'signal' ? 'FCR Signal Analysis' : (confidenceMode === 'evidence' ? 'FCR Signal Analysis (Indicative)' : 'FCR Signal Analysis'),
      // ★ FIX: noEvidenceLabel should NOT say "No deviation detected" when there IS a deviation signal
      // This is used when revenue risk calculation is not available, NOT when no deviation exists
      noEvidenceLabel: confidenceMode === 'signal' 
        ? 'Insufficient confidence for revenue estimate' 
        : (hasMissingFish 
          ? 'Revenue impact pending validation' 
          : 'No revenue impact identified'),
    },
    
    // Legacy snapshotLabels for backward compatibility
    snapshotLabels: isSnapshotMode ? {
      estimatedMissingFishLabel: 'Model-Implied Deviation',
      missingFishAssessmentLabel: 'Deviation Assessment',
      revenueRiskLabel: 'Deviation Revenue Impact (Unvalidated)',
      detectionReconciliationTitle: 'Deviation Detection Methods',
      reportTitle: 'Deviation Analysis Report',
      buttonLabel: 'Deviation Analysis',
      noEvidenceLabel: 'Insufficient data for estimate',
    } : null,
  };
  
  return overridden;
};

/**
 * validateSSOTOverride
 * 
 * Validates that the override was applied correctly.
 * Returns validation result with any issues found.
 */
export const validateSSOTOverride = (overriddenOutput) => {
  const issues = [];
  
  if (!overriddenOutput?._ssotOverrideApplied) {
    issues.push('SSOT override not applied');
    return { valid: false, issues };
  }
  
  const { _hasConflict, _wastedFeedKg, _detectionType } = overriddenOutput;
  
  // Validate conflict handling
  if (_hasConflict) {
    if (!overriddenOutput.conflictMessage?.includes('Conflicting signals')) {
      issues.push('Conflict message not applied');
    }
  }
  
  // Validate feeding classification
  if (_wastedFeedKg > 0 && overriddenOutput.feeding === 'Optimal') {
    issues.push('Feeding still shows "Optimal" despite wasted feed');
  }
  
  // Validate blocked patterns
  const patternsToCheck = _detectionType === 'missing_fish' ? BLOCKED_PATTERNS.missing_fish :
    _detectionType === 'normal' ? BLOCKED_PATTERNS.normal : [];
  
  const textFields = ['finalDetectionLabel', 'finalInterpretation', 'finalGrowthInsight'];
  textFields.forEach(field => {
    if (overriddenOutput[field] && !_hasConflict) {
      if (containsBlockedPattern(overriddenOutput[field], patternsToCheck)) {
        issues.push(`Blocked pattern found in ${field}: "${overriddenOutput[field]}"`);
      }
    }
  });
  
  return {
    valid: issues.length === 0,
    issues,
    overrideTimestamp: overriddenOutput._overrideTimestamp,
  };
};

/**
 * createRenderableReport
 * 
 * Convenience function that applies SSOT override and returns
 * a report object ready for rendering.
 */
export const createRenderableReport = (rawIntelligence) => {
  if (!rawIntelligence) return null;
  
  const { ssot, decisionContext } = rawIntelligence;
  
  // Build initial output from intelligence
  const output = {
    // Detection
    detection: ssot?.conclusion?.type,
    detectionLabel: ssot?.detectionStrength,
    missingFishCount: ssot?.missingFishCount,
    missingFishPercent: ssot?.missingFishPercent,
    // Interpretation
    interpretation: ssot?.conclusion?.interpretation,
    // Growth
    growthInsight: ssot?.conclusion?.growthInsight,
    jumpStatus: ssot?.jumpStatusMessage,
    // Feeding
    feeding: ssot?.feedingState,
    feedingExplanation: ssot?.feedingNote || ssot?.feedingImpact,
    // Financial
    financialConfidenceLevel: ssot?.financialConfidenceLevel,
    financialImpactEur: ssot?.financialImpactEur,
    wastedFeedKg: ssot?.wastedFeedKg,
    // Validation
    validation: decisionContext?.validation?.required ? decisionContext.validation : null,
    // Actions
    primaryAction: decisionContext?.primaryAction,
    secondaryActions: decisionContext?.secondaryActions,
    // Raw data for components that need it
    ssot,
    decisionContext,
  };
  
  // Apply SSOT override
  return applySSOTOverride(output, decisionContext, ssot);
};

export default applySSOTOverride;
